import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, AED_short } from '../lib/api';
import { useAuth } from '../lib/auth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, MapPin, TrendingUp, Home, Gavel, RefreshCw, Info, Clock,
  ExternalLink, Loader2, ChevronRight, Zap, ShieldCheck, ShieldAlert,
  ShieldX, Award, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import DataFreshnessBanner from '../components/DataFreshnessBanner';

/**
 * TopPicksPanel — the ONE consolidated Iris feed.
 * Replaces DealRadarPanel + HotListingsPanel + HotspotsPanel.
 *
 * Reads pre-computed iris_signals + iris_narrative from Mongo (instant).
 * User philosophy: single source of truth, deterministic scoring, AI narrates
 * only the top-50 nightly, refresh button gated to Iris admin.
 */

const FILTERS = [
  { key: 'all',        label: 'All',        icon: null },
  { key: 'flip',       label: 'Flip picks', icon: TrendingUp },
  { key: 'hold',       label: 'Hold picks', icon: Home },
  { key: 'auctions',   label: 'Auctions',   icon: Gavel },
  { key: 'watchlist',  label: 'Watchlist',  icon: Award },
];

const SORTS = [
  { key: 'score',       label: 'Hotness' },
  { key: 'discount',    label: 'Deepest discount' },
  { key: 'yield',       label: 'Highest yield' },
  { key: 'ending_soon', label: 'Ending soon' },
  { key: 'confidence',  label: 'Highest confidence' },
];

const CLASS_FILTERS = [
  { key: 'any',                     label: 'Any class' },
  { key: 'residential_secondary',   label: 'Resi Secondary' },
  { key: 'residential_offplan',     label: 'Resi Off-plan' },
  { key: 'plot',                    label: 'Plot' },
  { key: 'commercial',              label: 'Commercial' },
];

const CLASS_CHIP_STYLES = {
  residential_secondary: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  residential_offplan:   'bg-sky-50 text-sky-700 border-sky-200',
  plot:                  'bg-amber-50 text-amber-700 border-amber-200',
  commercial:            'bg-purple-50 text-purple-700 border-purple-200',
  commercial_plot:       'bg-purple-50 text-purple-700 border-purple-200',
  hospitality:           'bg-pink-50 text-pink-700 border-pink-200',
  special:               'bg-slate-100 text-slate-700 border-slate-200',
};

const CLASS_CHIP_LABELS = {
  residential_secondary: 'Secondary',
  residential_offplan:   'Off-plan',
  plot:                  'Plot',
  commercial:            'Commercial',
  commercial_plot:       'Ind. Land',
  hospitality:           'Hospitality',
  special:               'Special',
};

const IRIS_ADMIN_EMAILS = new Set(['shishir@irisrealty.com']);

function humanAgo(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function TopPicksPanel() {
  const { user } = useAuth();
  const isAdmin = IRIS_ADMIN_EMAILS.has((user?.email || '').toLowerCase());
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('score');
  const [expanded, setExpanded] = useState(50);
  const [meta, setMeta] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [ticker, setTicker] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [classFilter, setClassFilter] = useState('any');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ filter, sort, limit: '50' });
      if (selectedCommunity) params.set('community', selectedCommunity);
      const r = await api.get(`/iris/top-picks?${params}`);
      setPicks(r.data.picks || []);
      setMeta({
        model_version: r.data.model_version,
        regime: r.data.regime,
        last_scored_at: r.data.last_scored_at,
        last_narrated_at: r.data.last_narrated_at,
      });
    } catch (e) {
      toast.error('Failed to load top picks');
    } finally {
      setLoading(false);
    }
  }, [filter, sort, selectedCommunity]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/iris/community-ticker?top=10').then(r => setTicker(r.data.items || [])).catch(() => {});
  }, []);

  const doRefresh = async () => {
    if (!isAdmin) return;
    setRefreshing(true);
    setSyncProgress({ phase: 'starting', pct: 0, scored: 0, total: 0 });
    try {
      await api.post('/iris/refresh?include_market_sync=false&include_narrate=true');
      toast.success('Iris refresh queued — scoring in background', { duration: 4000 });
      // Poll for progress AND completion (max ~5 min for large corpora)
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          // v4 — new detailed progress endpoint for score_all
          try {
            const p = await api.get('/iris/refresh/status?job=score_all');
            if (p.data && p.data.phase && p.data.phase !== 'never_run') {
              setSyncProgress({
                phase: p.data.phase,
                scored: p.data.scored || 0,
                total: p.data.total || 0,
                pct: p.data.pct || 0,
                model_version: p.data.model_version,
              });
            }
          } catch { /* status endpoint may not exist on old backends */ }
          // Legacy completion check
          const s = await api.get('/trends/sync-status');
          const job = (s.data.jobs || []).find(j => j.job === 'iris_refresh');
          if (job?.status === 'done') {
            clearInterval(poll);
            setRefreshing(false);
            setSyncProgress(null);
            toast.success('Iris model refreshed');
            load();
          } else if (job?.status === 'error') {
            clearInterval(poll);
            setRefreshing(false);
            setSyncProgress(null);
            toast.error('Refresh failed');
          } else if (attempts > 100) {  // ~5 min at 3s poll
            clearInterval(poll);
            setRefreshing(false);
            setSyncProgress(null);
            toast.info('Still running in background — reload in a minute');
          }
        } catch { /* poll error is transient */ }
      }, 3000);
    } catch (e) {
      setRefreshing(false);
      setSyncProgress(null);
      toast.error(e.response?.data?.detail || 'Refresh failed');
    }
  };

  const visible = useMemo(() => {
    const base = classFilter === 'any'
      ? picks
      : picks.filter(p => (p.iris_class || 'residential_secondary') === classFilter);
    return base.slice(0, expanded);
  }, [picks, classFilter, expanded]);

  return (
    <div className="mb-8" data-testid="top-picks-panel">
      {/* ── Section header — accent card (like the old Deal Radar hero) ── */}
      <div className="mb-5 rounded-3xl bg-gradient-to-br from-[#1A2530] via-[#2a4038] to-[#4d7266] text-white shadow-lg overflow-hidden relative">
        {/* Decorative orb */}
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/5 pointer-events-none"/>
        <div className="absolute -right-16 -bottom-16 w-56 h-56 rounded-full bg-[#679286]/20 blur-2xl pointer-events-none"/>

        <div className="p-6 flex items-start justify-between flex-wrap gap-3 relative">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-300 font-semibold mb-2">
              <Zap size={12}/> Iris Top Picks
            </div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-white">Deals to act on today</h2>
            <div className="text-xs text-white/80 mt-2 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <ShieldCheck size={11}/> Model {meta.model_version || '—'}
              </span>
              <span className="opacity-50">·</span>
              <span>Scored {meta.last_scored_at ? humanAgo(meta.last_scored_at) : 'warming up'}</span>
              {meta.last_narrated_at && <><span className="opacity-50">·</span><span>Narrated {humanAgo(meta.last_narrated_at)}</span></>}
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={doRefresh}
              disabled={refreshing}
              data-testid="iris-refresh-btn"
              className="px-4 py-2.5 rounded-2xl bg-white/15 hover:bg-white/25 backdrop-blur border border-white/20 text-white text-sm font-semibold shadow-md flex items-center gap-2 disabled:opacity-60 active:scale-[0.98] transition"
            >
              {refreshing ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
              {refreshing ? 'Refreshing…' : 'Refresh Iris'}
            </button>
          )}
        </div>

        {/* v4 — live sync progress bar (admin-only, only visible during refresh) */}
        {refreshing && syncProgress && (
          <div className="px-6 pb-5 relative" data-testid="iris-sync-progress">
            <div className="rounded-xl bg-white/10 border border-white/20 p-3 backdrop-blur">
              <div className="flex items-center justify-between text-xs text-white/90 mb-2">
                <span className="font-semibold uppercase tracking-widest text-emerald-200">
                  {syncProgress.phase.replace(/_/g, ' ')}
                </span>
                <span className="tabular-nums">
                  {syncProgress.total > 0
                    ? `${syncProgress.scored.toLocaleString()} / ${syncProgress.total.toLocaleString()} · ${syncProgress.pct.toFixed(1)}%`
                    : 'preparing…'}
                </span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-200 transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(100, Math.max(2, syncProgress.pct || 0))}%` }}
                  data-testid="iris-sync-progress-fill"
                />
              </div>
              {syncProgress.model_version && (
                <div className="text-[10px] text-white/60 mt-1.5 tracking-wide">
                  Model {syncProgress.model_version}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Community ticker strip ── */}
      {ticker.length > 0 && (
        <div className="mb-5 -mx-1 px-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2 flex items-center gap-1">
            <Award size={10}/> Highest-yield communities right now
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {ticker.map(t => (
              <button
                key={t.community}
                onClick={() => setSelectedCommunity(selectedCommunity === t.community ? null : t.community)}
                data-testid={`ticker-${t.community.replace(/\s+/g, '-')}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 border transition ${
                  selectedCommunity === t.community
                    ? 'bg-[#679286] text-white border-[#679286] shadow-md'
                    : 'bg-white/70 border-white/70 text-slate-700 hover:border-[#679286]/50'
                }`}
              >
                <span className="opacity-70">🏆</span>
                <span>{t.community}</span>
                <span className={selectedCommunity === t.community ? 'text-emerald-100' : 'text-emerald-600'}>
                  {t.gross_yield_pct}%
                </span>
              </button>
            ))}
            {selectedCommunity && (
              <button
                onClick={() => setSelectedCommunity(null)}
                className="shrink-0 px-3 py-1.5 rounded-full text-xs text-slate-500 border border-transparent hover:border-slate-300"
              >
                Clear filter ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* Freshness warning for scoring pipeline (compact — Top Picks lives inside Trends) */}
      <div className="mb-3">
        <DataFreshnessBanner sources={['iris_scoring']} compact/>
      </div>

      {/* ── Filters + Sort ── */}
      <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="top-picks-filters">
          {FILTERS.map(f => {
            const Icon = f.icon;
            const on = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => { setFilter(f.key); setExpanded(50); }}
                data-testid={`top-picks-filter-${f.key}`}
                className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition inline-flex items-center gap-1.5 ${
                  on
                    ? 'bg-[#1A2530] text-white shadow-md'
                    : 'bg-white/70 border border-white/70 text-slate-700 hover:bg-white'
                }`}
              >
                {Icon && <Icon size={12}/>}
                {f.label}
              </button>
            );
          })}
        </div>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          data-testid="top-picks-sort"
          className="px-3 py-2 rounded-full bg-white/85 border border-white/70 text-xs font-medium text-slate-700 outline-none cursor-pointer hover:bg-white"
        >
          {SORTS.map(s => <option key={s.key} value={s.key}>{`Sort: ${s.label}`}</option>)}
        </select>
        <select
          value={classFilter}
          onChange={e => setClassFilter(e.target.value)}
          data-testid="top-picks-class-filter"
          className="px-3 py-2 rounded-full bg-white/85 border border-white/70 text-xs font-medium text-slate-700 outline-none cursor-pointer hover:bg-white"
        >
          {CLASS_FILTERS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>

      {/* ── Grid ── */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-72 rounded-2xl bg-white/50 border border-white/60 animate-pulse"/>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="glass p-12 text-center">
          <Sparkles size={28} className="mx-auto text-slate-300 mb-3"/>
          <div className="font-serif text-xl mb-2">No picks yet in this filter</div>
          <div className="text-xs text-slate-500 max-w-md mx-auto">
            {meta.last_scored_at
              ? 'Try switching filters or clearing the community selection.'
              : 'Iris is warming up — the first scoring pass runs every 4h. Admin can trigger a manual refresh above.'}
          </div>
        </div>
      ) : (
        <>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            data-testid="top-picks-grid"
          >
            <AnimatePresence>
              {visible.map((p, i) => (
                <PickCard key={`${p.source}-${p.source_id}`} p={p} rank={i + 1}/>
              ))}
            </AnimatePresence>
          </div>
          {picks.length > expanded && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setExpanded(x => x + 20)}
                className="px-6 py-3 rounded-full bg-white/80 border border-white/70 text-sm font-semibold text-slate-700 hover:bg-white active:scale-[0.98] transition"
              >
                Show more ({picks.length - expanded} more)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Individual pick card ────────────────────────────────────────────

function ConfidencePill({ confidence, ghost }) {
  if (ghost) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-red-100 text-red-700 border border-red-200">
      <ShieldX size={9}/> Ghost market
    </span>
  );
  const map = {
    high:   { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: ShieldCheck },
    medium: { cls: 'bg-amber-100 text-amber-700 border-amber-200',       Icon: ShieldAlert },
    low:    { cls: 'bg-slate-100 text-slate-600 border-slate-200',       Icon: ShieldAlert },
  };
  const cfg = map[confidence] || map.low;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border ${cfg.cls}`}>
      <Icon size={9}/> {confidence || 'low'}
    </span>
  );
}

function ExitBiasBadge({ bias }) {
  const map = {
    flip:    { label: 'FLIP',     cls: 'bg-orange-500 text-white' },
    hold:    { label: 'HOLD',     cls: 'bg-emerald-600 text-white' },
    either:  { label: 'FLIP/HOLD', cls: 'bg-sky-600 text-white' },
    auction: { label: 'AUCTION',  cls: 'bg-purple-600 text-white' },
  };
  const cfg = map[bias] || { label: '—', cls: 'bg-slate-500 text-white' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function PickCard({ p, rank }) {
  const s = p.signals || {};
  const score = Math.round(p.score || 0);
  const discount = s.discount_vs_sold_pct;
  const gy = s.gross_yield_pct;
  const scoreColor = score >= 80 ? 'from-emerald-500 to-teal-600'
                   : score >= 65 ? 'from-amber-500 to-orange-500'
                   : 'from-slate-400 to-slate-600';

  const detailHref = `/app/explain/${p.source}/${p.source_id}`;
  const isAuction = p.source === 'auction';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      layout
      className="rounded-2xl overflow-hidden bg-white/90 border border-white/70 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col"
      data-testid={`pick-card-${p.source}-${p.source_id}`}
    >
      {/* Cover image */}
      <Link to={detailHref} className="block relative h-40 bg-gradient-to-br from-slate-200 to-slate-300 shrink-0">
        {p.cover_photo_url ? (
          <img src={p.cover_photo_url} alt="" className="w-full h-full object-cover" loading="lazy"/>
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-400">
            {isAuction ? <Gavel size={30}/> : <Home size={30}/>}
          </div>
        )}
        {/* Score circle */}
        <div className={`absolute top-3 left-3 w-14 h-14 rounded-full bg-gradient-to-br ${scoreColor} grid place-items-center text-white shadow-lg`}>
          <div className="text-center leading-none">
            <div className="font-serif text-xl">{score}</div>
            <div className="text-[7px] uppercase tracking-widest opacity-90">score</div>
          </div>
        </div>
        {/* Rank pill */}
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-bold">#{rank}</div>
        {/* Bottom: bias + confidence */}
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
          <ExitBiasBadge bias={s.exit_bias}/>
          <ConfidencePill confidence={s.data_confidence} ghost={s.ghost_market}/>
        </div>
      </Link>

      {/* Body */}
      <Link to={detailHref} className="p-4 flex-1 flex flex-col gap-2">
        <div className="min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 min-w-0">
              <MapPin size={9}/>
              <span className="truncate">{p.community || '—'}</span>
            </div>
            {p.iris_class && CLASS_CHIP_LABELS[p.iris_class] && (
              <span
                data-testid={`pick-class-${p.iris_class}`}
                className={`shrink-0 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${CLASS_CHIP_STYLES[p.iris_class] || CLASS_CHIP_STYLES.special}`}
              >
                {CLASS_CHIP_LABELS[p.iris_class]}
              </span>
            )}
          </div>
          <div className="font-medium text-slate-900 line-clamp-2 text-sm min-h-[2.5rem] leading-tight">
            {p.title || p.building || 'Untitled listing'}
          </div>
        </div>

        {/* Verdict from narrative */}
        {p.narrative?.verdict && (
          <div className="text-[11px] text-slate-700 line-clamp-2 leading-snug italic">
            &ldquo;{p.narrative.verdict}&rdquo;
          </div>
        )}

        {/* Stats row */}
        <div className="mt-auto pt-2 border-t border-slate-100 grid grid-cols-3 gap-1 text-[10px]">
          <div>
            <div className="text-slate-400 uppercase tracking-widest">Ask</div>
            <div className="font-semibold text-slate-900">{AED_short(p.price_aed)}</div>
          </div>
          <div>
            <div className="text-slate-400 uppercase tracking-widest">
              {isAuction ? 'Ends' : 'vs Sold'}
            </div>
            <div className={`font-semibold ${
              isAuction ? 'text-purple-700' :
              (discount ?? 0) < 0 ? 'text-emerald-600' :
              (discount ?? 0) > 5 ? 'text-red-500' : 'text-slate-700'
            }`}>
              {isAuction && s.hours_until_close != null ? `${s.hours_until_close}h`
               : discount != null ? `${discount > 0 ? '+' : ''}${discount}%`
               : '—'}
            </div>
          </div>
          <div>
            <div className="text-slate-400 uppercase tracking-widest">Yield</div>
            <div className="font-semibold text-slate-900">
              {gy != null ? `${gy}%` : '—'}
            </div>
          </div>
        </div>
      </Link>

      {/* Footer actions */}
      <div className="p-3 pt-2 flex gap-2 border-t border-slate-100">
        <Link
          to={detailHref}
          className="flex-1 px-3 py-2 rounded-full bg-[#679286] hover:bg-[#4d7266] text-white text-[11px] font-semibold text-center inline-flex items-center justify-center gap-1 transition"
          data-testid={`pick-explain-${p.source_id}`}
        >
          <Sparkles size={11}/> Why?
        </Link>
        {p.listing_url && (
          <a
            href={p.listing_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-[11px] font-semibold inline-flex items-center gap-1"
            title={isAuction ? 'Emirates Auction' : 'Source listing'}
          >
            {isAuction ? 'Bid' : 'Source'} <ExternalLink size={10}/>
          </a>
        )}
      </div>
    </motion.div>
  );
}
